/**
 * Der Dialog im Fenster — nie `window.confirm`.
 *
 * Der Browser-Dialog friert die Seite ein, sieht in jedem Browser anders aus,
 * laesst sich nicht beschriften und trägt keine Pflichtfelder. Genau die
 * braucht diese Anwendung aber: „Ablehnen" ohne Grund gibt es nicht, und
 * „bezahlt" ohne Datum auch nicht. Uebernommen aus `finance-web/components/
 * ui/Modal.tsx`, um Fussleiste und Escape-Verhalten ergaenzt.
 *
 * `abbrechenBeiHintergrundklick=false` fuer Dialoge, in denen schon getippt
 * wurde: ein Klick daneben soll nicht eine halbe Begruendung wegwerfen.
 */

import { ReactNode, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { X } from 'lucide-react';

interface DialogProps {
  offen: boolean;
  onSchliessen: () => void;
  titel: string;
  /** Ein Satz unter der Ueberschrift — wozu der Dialog da ist. */
  hinweis?: string;
  children?: ReactNode;
  /** Knoepfe unten rechts. */
  fussleiste?: ReactNode;
  breite?: 'sm' | 'md' | 'lg';
  abbrechenBeiHintergrundklick?: boolean;
}

export function Dialog({
  offen,
  onSchliessen,
  titel,
  hinweis,
  children,
  fussleiste,
  breite = 'md',
  abbrechenBeiHintergrundklick = true,
}: DialogProps) {
  const kasten = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;

    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSchliessen();
    };
    document.addEventListener('keydown', beiTaste);
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Der Fokus gehoert in den Dialog, sonst tippt man weiter in die Seite
    // dahinter — und Tastaturbedienung ist bei Pflichtfeldern kein Beiwerk.
    const erstesFeld = kasten.current?.querySelector<HTMLElement>(
      'input, textarea, select, button',
    );
    erstesFeld?.focus();

    return () => {
      document.removeEventListener('keydown', beiTaste);
      document.body.style.overflow = vorher;
    };
  }, [offen, onSchliessen]);

  if (!offen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div
        className="fixed inset-0 bg-slate-900/40"
        onClick={abbrechenBeiHintergrundklick ? onSchliessen : undefined}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={kasten}
          className={clsx(
            'relative w-full rounded-xl bg-white shadow-soft-lg',
            breite === 'sm' && 'max-w-sm',
            breite === 'md' && 'max-w-lg',
            breite === 'lg' && 'max-w-2xl',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900">{titel}</h3>
              {hinweis && <p className="mt-0.5 text-sm text-slate-500">{hinweis}</p>}
            </div>
            <button
              type="button"
              onClick={onSchliessen}
              title="Schließen"
              className="-mr-2 -mt-1 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700"
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-6 py-5">{children}</div>

          {fussleiste && (
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
              {fussleiste}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
